import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { UploadIcon } from "lucide-react";
import { toast } from "@shared/ui/use-toast";
import {
  useEntityTypes,
  getEntityTypesWithoutPagination,
} from "../../../hooks";
import {
  useRemoteUtilityId,
  useTenantId,
} from "@shared/selectors/globalSelectors";
import { onboardingApi } from "@features/onboarding/utility-setup/api";
import { Step1Component } from "./Step1Component";
import { Step2Component } from "./Step2Component";
import { Step3Component } from "./Step3Component";
interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onClose }) => {
  // All your existing state variables here...
  const [step, setStep] = useState(1);
  const [selectedEntity, setSelectedEntity] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [validationData, setValidationData] = useState<any[]>([]);
  const [dataUploadId, setDataUploadId] = useState<number | null>(null);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<any>({});
  const [validationFilter, setValidationFilter] = useState("all");
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [bulkAssignValue, setBulkAssignValue] = useState("");
  const [parentEntityOptions, setParentEntityOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const tenantID = useTenantId();
  const remoteUtilityId = useRemoteUtilityId();

  // All your existing logic and useEffects here...
  const entityMapping = [
    { key: 3, value: "region" },
    { key: 4, value: "country" },
    { key: 5, value: "state" },
    { key: 6, value: "county" },
    { key: 7, value: "zone" },
    { key: 8, value: "division" },
    { key: 9, value: "area" },
    { key: 10, value: "sub_area" },
  ];

  const configLevel =
    entityMapping.find((item) => item.key === Number(selectedEntity))?.value ||
    "";

  const { data } = getEntityTypesWithoutPagination(configLevel);
  console.log("Entity config data:", data);
  const { data: entityTypesData } = useEntityTypes();
  console.log("Entity Types Data:", entityTypesData);

  const entityTypeOptions =
    entityTypesData?.result?.map((type) => ({
      value: type.key.toString(),
      label: type.value,
      id: type.key,
    })) || [];

  // useEffect(() => {
  //   console.log("Selected entity changed:", selectedEntity);
  //   console.log("Selected entity  Config level:", configLevel);
  //   console.log("data at useEffeact:", data);
  //   if (selectedEntity) {
  //     const parentOptions =
  //       data?.map((item: any) => ({
  //         value: item.name,
  //         label: item.name,
  //       })) || [];
  //       console.log("Selected entity  Parent entity options:", parentOptions);
  //     setParentEntityOptions(parentOptions);
  //     console.log("Selected entity  Parent entity options set at parent:", parentOptions);
  //   }
  //   else{
  //     console.log('Selected entity  parameter missing or isEditMode is false', { selectedEntity, isEditMode, data } );
  //   }
  // }, [selectedEntity]);

  useEffect(() => {
    console.log("Selected entity changed:", selectedEntity);
    console.log("Selected entity Config level:", configLevel);
    console.log("data at useEffect:", data);

    if (selectedEntity && data) {
      // Also check if data exists
      const parentOptions =
        data?.map((item: any) => ({
          value: item.name,
          label: item.name,
        })) || [];
      console.log("Selected entity Parent entity options:", parentOptions);
      setParentEntityOptions(parentOptions);
      console.log(
        "Selected entity Parent entity options set at parent:",
        parentOptions
      );
    } else {
      console.log("Selected entity parameter missing or data not loaded", {
        selectedEntity,
        data,
        configLevel,
      });
      // Clear parent options when no entity is selected or data is not available
      setParentEntityOptions([]);
    }
  }, [selectedEntity, data]); // Add 'data' to dependencies

  // All your existing handler functions here...
  const handleDownloadTemplate = async () => {
    if (!selectedEntity) return;
    setIsDownloading(true);
    try {
      const blob = await onboardingApi.downloadTemplate(
        "Territory",
        selectedEntity
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Territory_import_template.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      setHasDownloaded(true);
      toast({
        title: "Success",
        description: "Template downloaded successfully",
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to download template",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!selectedEntity || !remoteUtilityId) {
toast({
  title: "Error",
  description: "Please select entity type first",
  variant: "destructive",
});
      return;
    }
    setUploadedFile(file);
    setIsValidating(true);
    try {
      const selectedEntityData = entityTypeOptions.find(
        (e) => e.value === selectedEntity
      );
      if (!selectedEntityData) throw new Error("Invalid entity type");
      const response = await onboardingApi.validateData(
        Number(selectedEntityData.id),
        remoteUtilityId,
        tenantID,
        file
      );
      setDataUploadId(response.dataUploadId);
      setValidationData(response.result);
      setStep(3);
    } catch (error: any) {
      toast({
  title: "Error",
  description: "Failed to validate file",
  variant: "destructive",
});
      setUploadedFile(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (!dataUploadId) {
toast({
  title: "Error",
  description: "Failed to upload",
  variant: "destructive",
});
      return;
    }
    setIsUploading(true);
    try {
      await onboardingApi.uploadData(dataUploadId, remoteUtilityId, tenantID);
toast({
  title: "Success",
  description: "Successfully uploaded",
  variant: "default",
});
      resetModal();
      onClose();
    } catch (error: any) {
toast({
  title: "Upload Failed",
  description: "Failed to upload",
  variant: "destructive",
});
    } finally {
      setIsUploading(false);
    }
  };

  const resetModal = () => {
    setStep(1);
    setSelectedEntity("");
    setUploadedFile(null);
    setValidationData([]);
    setDataUploadId(null);
    // setEditingRow(null);
    // setEditValues({});
    setValidationFilter("all");
    setIsValidating(false);
    setIsUploading(false);
    setIsDownloading(false);
    setHasDownloaded(false);
    setIsEditMode(false);
    setSelectedRows(new Set());
    setBulkAssignValue("");
    setParentEntityOptions([]);
  };

  const filteredValidationData = validationData.filter((row) => {
    if (validationFilter === "all") return true;
    return row.status === validationFilter;
  });

  const getTableColumns = () => {
    if (validationData.length === 0) return [];
    const firstRow = validationData[0];
    return Object.keys(firstRow);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && (onClose(), resetModal())}
    >
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UploadIcon className="w-5 h-5" />
            Import Service Area Data - Step {step} of 3
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <Step1Component
            selectedEntity={selectedEntity}
            setSelectedEntity={setSelectedEntity}
            entityTypeOptions={entityTypeOptions}
            handleDownloadTemplate={handleDownloadTemplate}
            isDownloading={isDownloading}
            hasDownloaded={hasDownloaded}
            setStep={setStep}
          />
        )}

        {step === 2 && (
          <Step2Component
            handleFileUpload={handleFileUpload}
            isValidating={isValidating}
            uploadedFile={uploadedFile}
            setStep={setStep}
          />
        )}

        {step === 3 && (
          <Step3Component
            validationData={validationData}
            filteredValidationData={filteredValidationData}
            getTableColumns={getTableColumns}
            handleImport={handleImport}
            isUploading={isUploading}
            setStep={setStep}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            bulkAssignValue={bulkAssignValue}
            setBulkAssignValue={setBulkAssignValue}
            parentEntityOptions={parentEntityOptions}
            // editingRow={editingRow}
            // setEditingRow={setEditingRow}
            // editValues={editValues}
            // setEditValues={setEditValues}
            setValidationData={setValidationData}
            dataUploadId={dataUploadId}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportModal;